import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useActionLock, useIdemKey } from '@breaker/action-lock';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import {
  useLoad,
  useLoadHistory,
  useLoadPods,
  useTrackingLinks,
  type LoadPodWithUrl,
  type LoadStopArrival,
} from '@/data/loads';
import { Card } from '@/components/ui/card';
import { useDrivers, useFleetMembers, indexMembers } from '@/data/members';
import { useDriversList } from '@/data/drivers';
import { useVehicles } from '@/data/vehicles';
import { vehicleTypeLabel } from '@/lib/vehicle-labels';
import { useLoadLocations } from '@/data/locations';
import { useCustomer } from '@/data/customers';
import {
  useLoadItems,
  useCreateLoadItem,
  useUpdateLoadItem,
  useDeleteLoadItem,
} from '@/data/load-items';
import {
  useTransitionLoad,
  useCancelLoad,
  useDuplicateLoad,
  useAssignLoad,
  useCreateTrackingLink,
  useRevokeTrackingLink,
  useAddStop,
  useUpdateStop,
  useUpdateLoad,
  useAdvanceStop,
} from '@/data/mutations';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/auth/api';
import { forwardAction, stepBack, isTerminal, canCancel, canFail } from '@/lib/state-machine';
import { readyToAssign } from '@/lib/readiness';
import {
  statusLabel,
  historyLabel,
  historyTone,
  motionLabel,
  stopStatusLabel,
  stopStatusTone,
} from '@/lib/status';
import { formatDateTime, formatMoney, formatRelative, formatTime, shortId } from '@/lib/format';
import { cn } from '@/lib/cn';
import { LocationMap, type MapStopPin } from '@/components/location-map';
import { config } from '@/config';
import type {
  DriverListEntry,
  Load,
  LoadItem,
  LoadStatus,
  LoadStop,
  LoadStopStatus,
  LoadTrackingLink,
  LoadTrackingLinkWithToken,
  LoadStatusHistoryEntry,
  FleetMemberWithUser,
  Vehicle,
} from '@breaker/shared-types';

export function LoadDetailPage() {
  const { loadId } = useParams({ from: '/authed/loads/$loadId' });
  const { fleet } = useActiveFleet();
  const fleetId = fleet?.id ?? null;

  const query = useLoad(fleetId, loadId);

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    const status = (query.error as ApiError | null)?.status;
    if (status === 404) {
      return (
        <div className="px-8 py-10 max-w-3xl">
          <EmptyState
            title="Load not found"
            description="It may have been deleted or you don't have access from this fleet."
            action={
              <Link to="/">
                <Button variant="secondary" size="sm">Back to loads</Button>
              </Link>
            }
          />
        </div>
      );
    }
    return (
      <div className="px-8 py-10 max-w-3xl">
        <EmptyState
          title="Couldn't load load"
          description={(query.error as Error)?.message ?? 'The server returned an error.'}
          action={
            <Button onClick={() => query.refetch()} variant="secondary" size="sm">
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <LoadDetail
      fleetId={fleetId!}
      load={query.data.load}
      stops={query.data.stops}
      arrivals={query.data.arrivals ?? []}
    />
  );
}

interface LoadDetailProps {
  fleetId: string;
  load: Load;
  stops: LoadStop[];
  arrivals: LoadStopArrival[];
}

function LoadDetail({ fleetId, load, stops, arrivals }: LoadDetailProps) {
  const sortedStops = [...stops].sort((a, b) => a.sequence - b.sequence);
  const pickups = sortedStops.filter((s) => s.type === 'pickup');
  const dropoffs = sortedStops.filter((s) => s.type === 'dropoff');

  return (
    <div className="px-4 py-6 lg:px-8 lg:py-10 max-w-[1100px]">
      <nav className="text-xs text-breaker-mute mb-4">
        <Link to="/" className="hover:text-breaker-ink">Loads</Link>
        <span className="mx-1.5">/</span>
        <span className="text-breaker-ink-2">{load.reference}</span>
      </nav>

      <header className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h1 className="font-mono text-2xl font-semibold tracking-tight text-breaker-ink">
              {load.reference}
            </h1>
            <StatusBadge status={load.status} />
            {load.status === 'assigned' && load.motion ? (
              <Badge tone="active">{motionLabel(load.motion)}</Badge>
            ) : null}
          </div>
          <p className="text-sm text-breaker-mute">
            Created {formatDateTime(load.createdAt)}
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <StatusActionsCard fleetId={fleetId} load={load} />
          <StopsCard
            fleetId={fleetId}
            loadId={load.id}
            loadStatus={load.status}
            pickups={pickups}
            dropoffs={dropoffs}
          />
          <ChargesCard fleetId={fleetId} load={load} />
          <DriverLocationCard fleetId={fleetId} load={load} stops={sortedStops} />
          <ProofCard fleetId={fleetId} loadId={load.id} stops={sortedStops} />
          <TimelineCard fleetId={fleetId} load={load} arrivals={arrivals} />
        </div>

        <div className="space-y-6">
          <AssignCard fleetId={fleetId} load={load} stops={sortedStops} />
          <DetailsCard fleetId={fleetId} load={load} />
          <TrackingLinkCard fleetId={fleetId} loadId={load.id} />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Status actions card
// ============================================================

function StatusActionsCard({ fleetId, load }: { fleetId: string; load: Load }) {
  const toast = useToast();
  const transition = useTransitionLoad({ fleetId });
  const cancel = useCancelLoad({ fleetId });
  const duplicate = useDuplicateLoad(fleetId);
  const navigate = useNavigate();

  const [reasonDialog, setReasonDialog] = useState<
    { kind: 'cancel' | 'fail'; busy: boolean } | null
  >(null);

  // Mark-X (S80): ONE contextual forward action, a quiet step-back, red
  // WORDS for fail/cancel (design Law 3 — red fills only commitment).
  // Entering `assigned` is never a button here — assignment lives on the
  // Driver card behind the readiness gate.
  const fwd = forwardAction(load.status);
  const back = stepBack(load.status);
  const terminal = isTerminal(load.status);

  function performTransition(to: LoadStatus, reason?: string) {
    return transition
      .mutateAsync({ loadId: load.id, to, ...(reason ? { reason } : {}) })
      .then(() => toast.success(`Status: ${statusLabel(to)}`))
      .catch((err: unknown) => {
        const message =
          err instanceof ApiError ? err.message : 'Couldn\u2019t transition status';
        toast.error(message);
      });
  }

  if (terminal) {
    return (
      <Card title="Status">
        <p className="text-sm text-breaker-mute py-2">
          This load is in a terminal state and cannot be transitioned further.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Status">
      <div className="flex flex-wrap items-center gap-2">
        {fwd ? (
          <Button
            variant="primary"
            size="sm"
            loading={transition.isPending}
            action={() => performTransition(fwd.to)}
          >
            {fwd.label}
          </Button>
        ) : (
          <p className="text-sm text-breaker-mute py-1">
            Ready to place &mdash; assign a driver from the Driver card.
          </p>
        )}

        <Button
          variant="secondary"
          size="sm"
          action={() =>
            duplicate
              .mutateAsync(load.id)
              .then(({ load: draft }) => {
                toast.success(
                  `Duplicated as ${draft.reference} — set the schedule and dispatch.`,
                );
                void navigate({ to: '/loads/$loadId', params: { loadId: draft.id } });
              })
              .catch(() => toast.error('Duplicate failed. Try again in a moment.'))
          }
        >
          Duplicate
        </Button>
        <span className="flex-1" />

        {back ? (
          <Button
            variant="ghost"
            size="sm"
            loading={transition.isPending}
            action={() => performTransition(back.to)}
            className="text-breaker-mute"
          >
            {back.label}
          </Button>
        ) : null}
        {canFail(load.status) ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setReasonDialog({ kind: 'fail', busy: false })}
            className="text-breaker-red-deep hover:bg-breaker-red/10"
          >
            Mark failed
          </Button>
        ) : null}
        {canCancel(load.status) ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setReasonDialog({ kind: 'cancel', busy: false })}
            className="text-breaker-red-deep hover:bg-breaker-red/10"
          >
            Cancel load
          </Button>
        ) : null}
      </div>

      <ReasonDialog
        open={Boolean(reasonDialog)}
        kind={reasonDialog?.kind ?? 'cancel'}
        busy={cancel.isPending || transition.isPending}
        onClose={() => setReasonDialog(null)}
        onSubmit={(reason) => {
          if (!reasonDialog) return Promise.resolve();
          if (reasonDialog.kind === 'cancel') {
            return cancel
              .mutateAsync({ loadId: load.id, reason })
              .then(() => {
                toast.success('Load cancelled');
                setReasonDialog(null);
              })
              .catch((err: unknown) => {
                toast.error(err instanceof ApiError ? err.message : 'Cancel failed');
              });
          }
          const done = performTransition('failed', reason);
          setReasonDialog(null);
          return done;
        }}
      />
    </Card>
  );
}

interface ReasonDialogProps {
  open: boolean;
  kind: 'cancel' | 'fail';
  busy: boolean;
  onClose(): void;
  onSubmit(reason: string): Promise<unknown>;
}

function ReasonDialog({ open, kind, busy, onClose, onSubmit }: ReasonDialogProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Unit D: Enter-key re-submits are dropped while one is in flight.
  const submitLock = useActionLock();

  function handle(e: FormEvent) {
    e.preventDefault();
    const trimmed = reason.trim();
    if (trimmed.length < 5) {
      setError('Reason must be at least 5 characters.');
      return;
    }
    if (trimmed.length > 240) {
      setError('Reason must be at most 240 characters.');
      return;
    }
    setError(null);
    submitLock.run(() => onSubmit(trimmed));
  }

  // Reset when dialog opens.
  useMemo(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const title = kind === 'cancel' ? 'Cancel load' : 'Mark load as failed';
  const description =
    kind === 'cancel'
      ? 'Cancellation is permanent. Add a short reason for the audit trail.'
      : 'Failed loads cannot be resumed. Reason is required (5–240 chars).';

  return (
    <Dialog open={open} onClose={onClose} title={title} description={description}>
      <form onSubmit={handle} className="space-y-4">
        <Field
          label="Reason"
          htmlFor="reason"
          error={error ?? undefined}
          hint={!error ? `${reason.trim().length} / 240` : undefined}
        >
          <Textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={
              kind === 'cancel' ? 'Customer cancelled' : 'Vehicle broke down'
            }
            autoFocus
          />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={busy || submitLock.locked}
          >
            Close
          </Button>
          <Button
            type="submit"
            variant={kind === 'cancel' ? 'danger' : 'primary'}
            size="sm"
            loading={busy || submitLock.locked}
          >
            {kind === 'cancel' ? 'Confirm cancel' : 'Mark as failed'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ============================================================
// Stops card
// ============================================================

function StopsCard({
  fleetId,
  loadId,
  loadStatus,
  pickups,
  dropoffs,
}: {
  fleetId: string;
  loadId: string;
  loadStatus: LoadStatus;
  pickups: LoadStop[];
  dropoffs: LoadStop[];
}) {
  const [form, setForm] = useState<{ mode: 'add' } | { mode: 'edit'; stop: LoadStop } | null>(null);
  const [itemForm, setItemForm] = useState<{ mode: 'add'; stopId: string } | { mode: 'edit'; item: LoadItem } | null>(null);
  const [advanceTarget, setAdvanceTarget] = useState<LoadStop | null>(null);
  const empty = pickups.length === 0 && dropoffs.length === 0;

  // C3: the dispatcher override is only offered on assigned loads with a
  // stop that still has a rung to climb.
  const canAdvance = loadStatus === 'assigned';

  const itemsQuery = useLoadItems(fleetId, loadId);
  const itemsByStop = useMemo(() => {
    const map = new Map<string, LoadItem[]>();
    for (const it of itemsQuery.data ?? []) {
      const arr = map.get(it.loadStopId);
      if (arr) arr.push(it);
      else map.set(it.loadStopId, [it]);
    }
    return map;
  }, [itemsQuery.data]);

  return (
    <Card title="Stops">
      <div className="flex justify-end -mt-1 mb-3">
        <Button size="sm" variant="secondary" onClick={() => setForm({ mode: 'add' })}>
          Add stop
        </Button>
      </div>

      {empty ? (
        <p className="text-sm text-breaker-mute py-4 text-center">
          No stops on this load yet.
        </p>
      ) : (
        <div className="space-y-5">
          {pickups.length > 0 ? (
            <StopGroup
              label="Pickup"
              stops={pickups}
              itemsByStop={itemsByStop}
              canAdvance={canAdvance}
              onEdit={(stop) => setForm({ mode: 'edit', stop })}
              onAddItem={(stopId) => setItemForm({ mode: 'add', stopId })}
              onEditItem={(item) => setItemForm({ mode: 'edit', item })}
              onAdvance={(stop) => setAdvanceTarget(stop)}
            />
          ) : null}
          {dropoffs.length > 0 ? (
            <StopGroup
              label="Dropoff"
              stops={dropoffs}
              itemsByStop={itemsByStop}
              canAdvance={canAdvance}
              onEdit={(stop) => setForm({ mode: 'edit', stop })}
              onAddItem={(stopId) => setItemForm({ mode: 'add', stopId })}
              onEditItem={(item) => setItemForm({ mode: 'edit', item })}
              onAdvance={(stop) => setAdvanceTarget(stop)}
            />
          ) : null}
        </div>
      )}

      <StopFormDialog
        fleetId={fleetId}
        loadId={loadId}
        open={Boolean(form)}
        stop={form?.mode === 'edit' ? form.stop : null}
        onClose={() => setForm(null)}
      />

      <AdvanceStopDialog
        fleetId={fleetId}
        loadId={loadId}
        stop={advanceTarget}
        onClose={() => setAdvanceTarget(null)}
      />

      <ItemFormDialog
        fleetId={fleetId}
        loadId={loadId}
        open={Boolean(itemForm)}
        mode={itemForm?.mode ?? 'add'}
        stopId={
          itemForm?.mode === 'add'
            ? itemForm.stopId
            : itemForm?.mode === 'edit'
              ? itemForm.item.loadStopId
              : null
        }
        item={itemForm?.mode === 'edit' ? itemForm.item : null}
        onClose={() => setItemForm(null)}
      />
    </Card>
  );
}

/** C3: the one-rung ladder the advance override climbs. */
const NEXT_STOP_STATUS: Partial<Record<LoadStopStatus, LoadStopStatus>> = {
  pending: 'en_route',
  en_route: 'arrived',
  arrived: 'completed',
};

function StopGroup({
  label,
  stops,
  itemsByStop,
  canAdvance,
  onEdit,
  onAddItem,
  onEditItem,
  onAdvance,
}: {
  label: string;
  stops: LoadStop[];
  itemsByStop: Map<string, LoadItem[]>;
  canAdvance: boolean;
  onEdit: (stop: LoadStop) => void;
  onAddItem: (stopId: string) => void;
  onEditItem: (item: LoadItem) => void;
  onAdvance: (stop: LoadStop) => void;
}) {
  return (
    <div>
      <p className="text-2xs font-medium uppercase tracking-wider text-breaker-ink-2 mb-2">
        {label} {stops.length > 1 ? `(${stops.length})` : null}
      </p>
      <ul className="space-y-2">
        {stops.map((s) => (
          <li
            key={s.id}
            className="group border-l-2 border-breaker-line-2 pl-4 py-1"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-breaker-ink">{s.line1}</p>
                  <Badge tone={stopStatusTone(s.status)}>{stopStatusLabel(s.status)}</Badge>
                  {s.arrivedAt || s.completedAt ? (
                    <span className="text-2xs text-breaker-mute">
                      {[
                        s.arrivedAt ? `arrived ${formatTime(s.arrivedAt)}` : null,
                        s.completedAt ? `done ${formatTime(s.completedAt)}` : null,
                      ]
                        .filter(Boolean)
                        .join(' \u00b7 ')}
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-breaker-mute mt-0.5">
                  {[s.line2, s.city, s.postcode].filter(Boolean).join(', ') || '\u2014'}
                </p>
                {s.contactName || s.contactPhoneE164 ? (
                  <p className="text-xs text-breaker-ink-2 mt-1">
                    {s.contactName ?? ''}
                    {s.contactName && s.contactPhoneE164 ? ' \u00B7 ' : ''}
                    {s.contactPhoneE164 ? (
                      <a
                        href={`tel:${s.contactPhoneE164}`}
                        className="text-breaker-identity hover:underline"
                      >
                        {s.contactPhoneE164}
                      </a>
                    ) : null}
                  </p>
                ) : null}
                {s.windowStartAt || s.windowEndAt ? (
                  <p className="text-2xs text-breaker-mute mt-1 uppercase tracking-wider">
                    Window: {formatDateTime(s.windowStartAt)} {'\u2013'} {formatDateTime(s.windowEndAt)}
                  </p>
                ) : null}
                {s.instructions ? (
                  <p className="text-xs text-breaker-ink-2 mt-1.5 italic">{s.instructions}</p>
                ) : null}
              </div>
              <div className="flex items-center gap-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                {canAdvance && NEXT_STOP_STATUS[s.status] ? (
                  <button
                    type="button"
                    onClick={() => onAdvance(s)}
                    className="text-2xs uppercase tracking-wider text-breaker-mute hover:text-breaker-identity"
                  >
                    Advance
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => onEdit(s)}
                  className="text-2xs uppercase tracking-wider text-breaker-mute hover:text-breaker-identity"
                >
                  Edit
                </button>
              </div>
            </div>
            <StopItems
              items={itemsByStop.get(s.id) ?? []}
              onAdd={() => onAddItem(s.id)}
              onEdit={onEditItem}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function StopItems({
  items,
  onAdd,
  onEdit,
}: {
  items: LoadItem[];
  onAdd: () => void;
  onEdit: (item: LoadItem) => void;
}) {
  return (
    <div className="mt-2">
      {items.length > 0 ? (
        <ul className="space-y-1 mb-1.5">
          {items.map((it) => (
            <li key={it.id} className="text-xs">
              <button
                type="button"
                onClick={() => onEdit(it)}
                className="text-left hover:text-breaker-identity"
              >
                <span className="text-breaker-ink-2">
                  {it.quantity > 1 ? `${it.quantity} \u00D7 ` : ''}
                  {it.description}
                </span>
                {it.unit || it.weightKg || it.reference ? (
                  <span className="text-breaker-mute">
                    {'  '}
                    {[
                      it.unit,
                      it.weightKg ? `${formatWeight(it.weightKg)} kg` : null,
                      it.reference ? `ref ${it.reference}` : null,
                    ]
                      .filter(Boolean)
                      .join(' \u00B7 ')}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <button
        type="button"
        onClick={onAdd}
        className="text-2xs uppercase tracking-wider text-breaker-mute hover:text-breaker-identity"
      >
        + Add goods
      </button>
    </div>
  );
}

function ItemFormDialog({
  fleetId,
  loadId,
  open,
  mode,
  stopId,
  item,
  onClose,
}: {
  fleetId: string;
  loadId: string;
  open: boolean;
  mode: 'add' | 'edit';
  stopId: string | null;
  item: LoadItem | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const createItem = useCreateLoadItem(fleetId, loadId);
  const updateItem = useUpdateLoadItem(fleetId, loadId);
  const deleteItem = useDeleteLoadItem(fleetId, loadId);
  const editing = mode === 'edit';
  // Unit D: Enter-key re-submits are dropped while one is in flight.
  const submitLock = useActionLock();
  const busy =
    createItem.isPending ||
    updateItem.isPending ||
    deleteItem.isPending ||
    submitLock.locked;

  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unit, setUnit] = useState('');
  const [weight, setWeight] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDescription(item?.description ?? '');
    setQuantity(item ? String(item.quantity) : '1');
    setUnit(item?.unit ?? '');
    setWeight(item?.weightKg ? formatWeight(item.weightKg) : '');
    setReference(item?.reference ?? '');
    setNotes(item?.notes ?? '');
  }, [open, item]);

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const desc = description.trim();
    if (!desc) {
      setError('Description is required.');
      return;
    }

    const qtyNum = Number.parseInt(quantity, 10);
    const qty = Number.isFinite(qtyNum) && qtyNum >= 1 ? qtyNum : 1;

    const weightStr = weight.trim();
    let weightKg: number | null = null;
    if (weightStr) {
      const w = Number(weightStr);
      weightKg = Number.isFinite(w) && w >= 0 ? w : null;
    }

    const onErr = (err: unknown) =>
      toast.error(err instanceof ApiError ? err.message : 'Couldn\u2019t save goods');

    if (editing && item) {
      submitLock.run(() =>
        updateItem
          .mutateAsync({
            itemId: item.id,
            body: {
              description: desc,
              quantity: qty,
              unit: unit.trim() || null,
              weightKg,
              reference: reference.trim() || null,
              notes: notes.trim() || null,
            },
          })
          .then(() => {
            toast.success('Goods updated');
            onClose();
          })
          .catch(onErr),
      );
    } else {
      if (!stopId) {
        setError('No stop selected.');
        return;
      }
      submitLock.run(() =>
        createItem
          .mutateAsync({
            loadStopId: stopId,
            description: desc,
            quantity: qty,
            unit: unit.trim() || null,
            weightKg,
            reference: reference.trim() || null,
            notes: notes.trim() || null,
          })
          .then(() => {
            toast.success('Goods added');
            onClose();
          })
          .catch(onErr),
      );
    }
  }

  function handleDelete() {
    if (!item) return Promise.resolve();
    return deleteItem
      .mutateAsync(item.id)
      .then(() => {
        toast.success('Goods removed');
        onClose();
      })
      .catch((err: unknown) =>
        toast.error(err instanceof ApiError ? err.message : 'Couldn\u2019t remove goods'),
      );
  }

  return (
    <Dialog open={open} onClose={onClose} title={editing ? 'Edit goods' : 'Add goods'}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Description" htmlFor="itemDescription" error={error ?? undefined}>
          <Input
            id="itemDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            invalid={Boolean(error)}
            placeholder="e.g. Pallet of A4 paper"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Quantity" htmlFor="itemQuantity">
            <Input
              id="itemQuantity"
              type="number"
              min={1}
              step={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </Field>
          <Field label="Unit" htmlFor="itemUnit" hint="Optional, e.g. boxes, pallets">
            <Input id="itemUnit" value={unit} onChange={(e) => setUnit(e.target.value)} />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Weight (kg)" htmlFor="itemWeight" hint="Optional">
            <Input
              id="itemWeight"
              type="number"
              min={0}
              step="0.001"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            />
          </Field>
          <Field label="Reference" htmlFor="itemReference" hint="Optional">
            <Input
              id="itemReference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </Field>
        </div>

        <Field label="Notes" htmlFor="itemNotes" hint="Handling notes, fragile, etc.">
          <Textarea
            id="itemNotes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>

        <div className="flex justify-between gap-2 pt-1">
          <div>
            {editing ? (
              <Button type="button" variant="ghost" action={handleDelete} disabled={busy}>
                Delete
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy}>
              {editing ? 'Save goods' : 'Add goods'}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function formatWeight(w: string): string {
  const n = Number(w);
  return Number.isFinite(n) ? String(n) : w;
}

// ============================================================
// Advance stop dialog (C3 — dispatcher override, one rung)
// ============================================================

function AdvanceStopDialog({
  fleetId,
  loadId,
  stop,
  onClose,
}: {
  fleetId: string;
  loadId: string;
  stop: LoadStop | null;
  onClose(): void;
}) {
  const toast = useToast();
  const advance = useAdvanceStop({ fleetId });
  const [reason, setReason] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  // Unit D: Enter-key re-submits are dropped while one is in flight, and the
  // API sees the same key for every retry of THIS rung — a new stop or a new
  // status is a new scope, which mints a fresh key automatically.
  const submitLock = useActionLock();
  const { key: idemKey } = useIdemKey(`advance:${stop?.id ?? ''}:${stop?.status ?? ''}`);

  const open = Boolean(stop);
  useEffect(() => {
    if (!open) return;
    setReason('');
    setApiError(null);
  }, [open]);

  const next = stop ? NEXT_STOP_STATUS[stop.status] : undefined;
  const valid = reason.trim().length >= 5;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!stop || !next || !valid) return;
    setApiError(null);
    submitLock.run(() =>
      advance
        .mutateAsync({
          loadId,
          stopId: stop.id,
          reason: reason.trim(),
          idempotencyKey: idemKey,
          expectedStatus: stop.status,
        })
        .then(() => {
          toast.success(`Stop advanced to ${stopStatusLabel(next!)}`);
          onClose();
        })
        .catch((err: unknown) => {
          setApiError(
            err instanceof ApiError ? err.message : 'Couldn’t advance the stop',
          );
        }),
    );
  }

  if (!stop || !next) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Advance this stop"
      description={`Moves the stop one rung — ${stopStatusLabel(stop.status)} → ${stopStatusLabel(next)} — recorded as a dispatcher override. This is the escape hatch when GPS fails at the gate.`}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field
          label="Reason"
          htmlFor="advanceReason"
          error={apiError ?? undefined}
          hint={!apiError ? 'Required — at least 5 characters, kept in the audit trail.' : undefined}
        >
          <Textarea
            id="advanceReason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Driver at the gate, no GPS signal"
            invalid={Boolean(apiError)}
            autoFocus
          />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={advance.isPending || submitLock.locked}
          >
            Close
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={!valid}
            loading={advance.isPending || submitLock.locked}
          >
            Advance to {stopStatusLabel(next)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ============================================================
// Proof card (C1 — PODs on the job sheet)
// ============================================================

function ProofCard({
  fleetId,
  loadId,
  stops,
}: {
  fleetId: string;
  loadId: string;
  stops: LoadStop[];
}) {
  const podsQuery = useLoadPods(fleetId, loadId);

  // Group per stop (in stop order), then everything else under "Load-level".
  const groups = useMemo(() => {
    const pods = podsQuery.data ?? [];
    const byStop = new Map<string, LoadPodWithUrl[]>();
    const loadLevel: LoadPodWithUrl[] = [];
    const stopIds = new Set(stops.map((s) => s.id as string));
    for (const pod of pods) {
      if (pod.loadStopId && stopIds.has(pod.loadStopId)) {
        const arr = byStop.get(pod.loadStopId);
        if (arr) arr.push(pod);
        else byStop.set(pod.loadStopId, [pod]);
      } else {
        loadLevel.push(pod);
      }
    }
    const out: Array<{ key: string; label: string; pods: LoadPodWithUrl[] }> = [];
    for (const s of stops) {
      const list = byStop.get(s.id);
      if (list && list.length > 0) {
        out.push({
          key: s.id,
          label: `${s.type === 'pickup' ? 'Collection' : 'Delivery'} — ${s.city ?? s.line1}`,
          pods: list,
        });
      }
    }
    if (loadLevel.length > 0) {
      out.push({ key: 'load-level', label: 'Load-level', pods: loadLevel });
    }
    return out;
  }, [podsQuery.data, stops]);

  return (
    <Card title="Proof">
      {podsQuery.isLoading ? (
        <div className="py-4 flex justify-center">
          <Spinner size="sm" />
        </div>
      ) : podsQuery.isError ? (
        <p className="text-sm text-breaker-mute">Couldn&rsquo;t load proof.</p>
      ) : groups.length > 0 ? (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.key}>
              <p className="text-2xs font-medium uppercase tracking-wider text-breaker-ink-2 mb-2">
                {g.label}
              </p>
              <PodGroup pods={g.pods} />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-breaker-mute py-2">
          No proof captured yet &mdash; it appears here the moment the driver
          captures it.
        </p>
      )}
    </Card>
  );
}

function PodGroup({ pods }: { pods: LoadPodWithUrl[] }) {
  const photos = pods.filter((p) => p.kind === 'photo');
  const rest = pods.filter((p) => p.kind !== 'photo');
  return (
    <div className="space-y-3">
      {photos.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((p) => (
            <PodPhotoTile key={p.id} pod={p} />
          ))}
        </div>
      ) : null}
      {rest.map((p) =>
        p.kind === 'signature' ? (
          <PodSignature key={p.id} pod={p} />
        ) : (
          <PodTextRow key={p.id} pod={p} />
        ),
      )}
    </div>
  );
}

/** Placeholder tile when the presigned URL is missing (10-min TTL ran out). */
function PodExpiredTile({ pod, className }: { pod: LoadPodWithUrl; className?: string }) {
  return (
    <div
      className={cn(
        'rounded-md border border-dashed border-breaker-line-2 bg-breaker-ground-2',
        'flex flex-col items-center justify-center text-center px-2 py-3',
        className,
      )}
    >
      <span className="text-2xs uppercase tracking-wider text-breaker-ink-2">
        {pod.kind}
      </span>
      <span className="text-2xs text-breaker-mute mt-0.5">
        link expired &mdash; reload
      </span>
    </div>
  );
}

function PodPhotoTile({ pod }: { pod: LoadPodWithUrl }) {
  return (
    <figure>
      {pod.url ? (
        <a href={pod.url} target="_blank" rel="noreferrer" className="block">
          <img
            src={pod.url}
            alt="Proof photo"
            className="h-24 w-full object-cover rounded-md border border-breaker-line"
          />
        </a>
      ) : (
        <PodExpiredTile pod={pod} className="h-24 w-full" />
      )}
      {pod.capturedAt ? (
        <figcaption className="text-2xs text-breaker-mute mt-1">
          {formatTime(pod.capturedAt)}
        </figcaption>
      ) : null}
    </figure>
  );
}

function PodSignature({ pod }: { pod: LoadPodWithUrl }) {
  return (
    <div>
      {pod.url ? (
        <a href={pod.url} target="_blank" rel="noreferrer" className="inline-block">
          <img
            src={pod.url}
            alt={pod.signerName ? `Signature — ${pod.signerName}` : 'Signature'}
            className="h-20 max-w-full bg-white object-contain rounded-md border border-breaker-line"
          />
        </a>
      ) : (
        <PodExpiredTile pod={pod} className="h-20 w-40" />
      )}
      <p className="text-xs text-breaker-ink-2 mt-1">
        {pod.signerName ? `Signed by ${pod.signerName}` : 'No name recorded'}
        {pod.capturedAt ? (
          <span className="text-breaker-mute"> · {formatTime(pod.capturedAt)}</span>
        ) : null}
      </p>
    </div>
  );
}

/** Note + barcode kinds — text rows, no file to show. */
function PodTextRow({ pod }: { pod: LoadPodWithUrl }) {
  return (
    <p className="text-xs text-breaker-ink-2">
      {pod.kind === 'note' ? (
        pod.noteText ? (
          <span className="italic">&ldquo;{pod.noteText}&rdquo;</span>
        ) : (
          <span className="italic text-breaker-mute">Note — no text recorded</span>
        )
      ) : (
        <>
          Barcode{' '}
          {pod.barcodeValue ? (
            <span className="ref">{pod.barcodeValue}</span>
          ) : (
            <span className="text-breaker-mute">— no value recorded</span>
          )}
        </>
      )}
      {pod.capturedAt ? (
        <span className="text-breaker-mute"> · {formatTime(pod.capturedAt)}</span>
      ) : null}
    </p>
  );
}

// ============================================================
// Timeline card (real history)
// ============================================================

type TimelineItem =
  | { kind: 'history'; at: string; entry: LoadStatusHistoryEntry }
  | { kind: 'override'; at: string; arrival: LoadStopArrival };

function TimelineCard({
  fleetId,
  load,
  arrivals,
}: {
  fleetId: string;
  load: Load;
  arrivals: LoadStopArrival[];
}) {
  const historyQuery = useLoadHistory(fleetId, load.id);
  const membersQuery = useFleetMembers(fleetId);

  // Use the full members list (drivers + others) to resolve actor names.
  const memberById = useMemo(() => indexMembers(membersQuery.data), [membersQuery.data]);

  // Merge status history with dispatcher-override markers (C3), newest first.
  const items = useMemo<TimelineItem[]>(() => {
    const history: TimelineItem[] = (historyQuery.data ?? []).map((entry) => ({
      kind: 'history',
      at: entry.createdAt,
      entry,
    }));
    const overrides: TimelineItem[] = arrivals
      .filter((a) => a.result === 'override')
      .map((arrival) => ({ kind: 'override', at: arrival.createdAt, arrival }));
    return [...history, ...overrides].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  }, [historyQuery.data, arrivals]);

  return (
    <Card title="Timeline">
      {historyQuery.isLoading ? (
        <div className="py-4 flex justify-center">
          <Spinner size="sm" />
        </div>
      ) : historyQuery.isError ? (
        <p className="text-sm text-breaker-mute">
          Couldn&rsquo;t load history.
        </p>
      ) : items.length > 0 ? (
        <ol className="relative">
          {items.map((item, idx, arr) =>
            item.kind === 'history' ? (
              <TimelineEntry
                key={item.entry.id}
                entry={item.entry}
                isFirst={idx === 0}
                isLast={idx === arr.length - 1}
                actorName={
                  item.entry.changedByUserId
                    ? memberById.get(item.entry.changedByUserId)?.user.displayName ?? null
                    : null
                }
              />
            ) : (
              <OverrideTimelineEntry
                key={item.arrival.id}
                arrival={item.arrival}
                isFirst={idx === 0}
                isLast={idx === arr.length - 1}
              />
            ),
          )}
        </ol>
      ) : (
        <p className="text-sm text-breaker-mute py-2">No history yet.</p>
      )}
    </Card>
  );
}

/** "advanced to arrived by dispatcher: <text>" -> the text after the colon. */
function overrideReasonText(reason: string | null): string | null {
  if (!reason) return null;
  const idx = reason.indexOf(': ');
  const text = idx >= 0 ? reason.slice(idx + 2).trim() : reason.trim();
  return text.length > 0 ? text : null;
}

function OverrideTimelineEntry({
  arrival,
  isFirst,
  isLast,
}: {
  arrival: LoadStopArrival;
  isFirst: boolean;
  isLast: boolean;
}) {
  const reason = overrideReasonText(arrival.reason);
  return (
    <li className="relative pl-7 pb-5 last:pb-0">
      {!isLast ? (
        <span className="absolute left-[10px] top-3 bottom-0 w-px bg-breaker-line" />
      ) : null}
      <span
        className={cn(
          'absolute left-[6px] top-1.5 h-2.5 w-2.5 rounded-full border-2',
          isFirst ? 'bg-breaker-identity border-breaker-identity' : 'bg-white border-breaker-line-2',
        )}
      />
      <div className="flex items-baseline gap-2 flex-wrap">
        <Badge tone="active">Override</Badge>
        <span className="text-2xs text-breaker-mute">
          {formatRelative(arrival.createdAt)} · {formatDateTime(arrival.createdAt)}
        </span>
      </div>
      <p className="text-xs text-breaker-ink-2 mt-1">
        Advanced by dispatcher{reason ? <> &mdash; &ldquo;{reason}&rdquo;</> : null}
      </p>
    </li>
  );
}

function TimelineEntry({
  entry,
  isFirst,
  isLast,
  actorName,
}: {
  entry: LoadStatusHistoryEntry;
  isFirst: boolean;
  isLast: boolean;
  actorName: string | null;
}) {
  return (
    <li className="relative pl-7 pb-5 last:pb-0">
      {!isLast ? (
        <span className="absolute left-[10px] top-3 bottom-0 w-px bg-breaker-line" />
      ) : null}
      <span
        className={cn(
          'absolute left-[6px] top-1.5 h-2.5 w-2.5 rounded-full border-2',
          isFirst ? 'bg-breaker-identity border-breaker-identity' : 'bg-white border-breaker-line-2',
        )}
      />
      <div className="flex items-baseline gap-2 flex-wrap">
        <Badge tone={historyTone(entry.toStatus)}>{historyLabel(entry.toStatus)}</Badge>
        <span className="text-2xs text-breaker-mute">
          {formatRelative(entry.createdAt)} · {formatDateTime(entry.createdAt)}
        </span>
      </div>
      <p className="text-xs text-breaker-ink-2 mt-1">
        {actorName ? (
          <span>{actorName}</span>
        ) : entry.changedByUserId ? (
          <span className="ref">{shortId(entry.changedByUserId)}</span>
        ) : (
          <span className="italic">system</span>
        )}
        {entry.fromStatus ? (
          <>
            <span className="mx-1.5">·</span>
            from <span className="text-breaker-ink-2">{historyLabel(entry.fromStatus)}</span>
          </>
        ) : null}
      </p>
      {entry.reason ? (
        <p className="text-xs text-breaker-ink-2 mt-1.5 italic max-w-prose">
          &ldquo;{entry.reason}&rdquo;
        </p>
      ) : null}
    </li>
  );
}

// ============================================================
// Assign card
// ============================================================

function AssignCard({ fleetId, load, stops }: { fleetId: string; load: Load; stops: LoadStop[] }) {
  const toast = useToast();
  const driversQuery = useDrivers(fleetId);
  const membersQuery = useFleetMembers(fleetId);
  const driversListQuery = useDriversList(fleetId);
  const vehiclesQuery = useVehicles(fleetId);
  const assign = useAssignLoad({ fleetId });
  const transition = useTransitionLoad({ fleetId });

  const [pickerOpen, setPickerOpen] = useState(false);

  // Mark-X gate (S80): entering `assigned` requires the load to be ready;
  // reassignment of an already-assigned load is deliberately ungated.
  const readiness = useMemo(() => readyToAssign(load, stops), [load, stops]);
  const gateApplies = !load.assignedDriverUserId;

  // C5: a draft that would pass every OTHER readiness check gets a one-click
  // "Confirm & assign" — the confirm transition, then straight to the picker.
  const confirmableDraft = useMemo(
    () =>
      load.status === 'draft' &&
      readyToAssign({ ...load, status: 'confirmed' }, stops).ready,
    [load, stops],
  );

  function confirmAndAssign() {
    return transition
      .mutateAsync({ loadId: load.id, to: 'confirmed' })
      .then(() => {
        toast.success('Load confirmed — pick a driver.');
        setPickerOpen(true);
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiError ? err.message : 'Couldn’t confirm the load');
      });
  }

  const currentDriver: FleetMemberWithUser | undefined = useMemo(() => {
    if (!load.assignedDriverUserId) return undefined;
    return membersQuery.data?.find((m) => m.userId === load.assignedDriverUserId);
  }, [load.assignedDriverUserId, membersQuery.data]);

  const currentVehicle = useMemo(() => {
    if (!load.assignedVehicleId) return undefined;
    return vehiclesQuery.data?.find((v) => v.id === load.assignedVehicleId);
  }, [load.assignedVehicleId, vehiclesQuery.data]);

  return (
    <Card title="Driver">
      {load.assignedDriverUserId ? (
        <div className="space-y-1">
          <p className="text-sm font-medium text-breaker-ink">
            {currentDriver?.user.displayName ?? (
              <span className="ref">{shortId(load.assignedDriverUserId)}</span>
            )}
          </p>
          {currentDriver ? (
            <p className="text-xs text-breaker-mute">@{currentDriver.user.handle}</p>
          ) : null}
          {/* U7: the per-job vehicle — the one the customer timeline and the
              public tracking page show, never the driver's default. */}
          {currentVehicle ? (
            <p className="text-xs text-breaker-ink-2">
              <span className="ref">{currentVehicle.registration}</span>{' '}
              <span className="text-breaker-mute">
                {vehicleTypeLabel(currentVehicle.type)}
              </span>
            </p>
          ) : load.assignedVehicleId ? (
            <p className="text-xs text-breaker-mute">
              <span className="ref">{shortId(load.assignedVehicleId)}</span>
            </p>
          ) : (
            <p className="text-xs text-breaker-mute italic">No vehicle on this job</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-breaker-mute italic">No driver assigned</p>
      )}

      {!isTerminal(load.status) ? (
        <div className="mt-3 pt-3 border-t border-breaker-line">
          {gateApplies && confirmableDraft ? (
            <Button
              variant="primary"
              size="sm"
              disabled={driversQuery.isLoading}
              action={confirmAndAssign}
            >
              Confirm &amp; assign
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPickerOpen(true)}
              disabled={driversQuery.isLoading || (gateApplies && !readiness.ready)}
            >
              {load.assignedDriverUserId ? 'Reassign' : 'Assign driver'}
            </Button>
          )}
          {gateApplies && !readiness.ready ? (
            <p className="text-xs text-breaker-mute mt-2">
              {confirmableDraft
                ? 'This load is still a draft — Confirm & assign runs the confirmation, then opens the driver picker.'
                : readiness.reason}
            </p>
          ) : null}
        </div>
      ) : null}

      <DriverPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentDriverId={load.assignedDriverUserId ?? null}
        currentVehicleId={load.assignedVehicleId ?? null}
        drivers={driversQuery.data ?? []}
        driverEntries={driversListQuery.data ?? []}
        vehicles={vehiclesQuery.data ?? []}
        busy={assign.isPending}
        onSelect={({ driverUserId, vehicleId }) =>
          assign
            .mutateAsync({ loadId: load.id, driverUserId, vehicleId })
            .then(() => {
              toast.success(driverUserId ? 'Driver assigned' : 'Driver unassigned');
              setPickerOpen(false);
            })
            .catch((err: unknown) => {
              toast.error(err instanceof ApiError ? err.message : 'Assign failed');
            })
        }
      />
    </Card>
  );
}

interface DriverPickerProps {
  open: boolean;
  onClose(): void;
  currentDriverId: string | null;
  currentVehicleId: string | null;
  drivers: FleetMemberWithUser[];
  driverEntries: DriverListEntry[];
  vehicles: Vehicle[];
  busy: boolean;
  onSelect(choice: { driverUserId: string | null; vehicleId: string | null }): Promise<unknown>;
}

function DriverPickerDialog({
  open,
  onClose,
  currentDriverId,
  currentVehicleId,
  drivers,
  driverEntries,
  vehicles,
  busy,
  onSelect,
}: DriverPickerProps) {
  const [selected, setSelected] = useState<string>('');
  const [vehicleId, setVehicleId] = useState<string>('');

  useMemo(() => {
    if (open) {
      setSelected(currentDriverId ?? '');
      setVehicleId(currentVehicleId ?? '');
    }
  }, [open, currentDriverId, currentVehicleId]);

  const entryFor = (userId: string) => driverEntries.find((d) => d.userId === userId);

  function onDriverChange(userId: string) {
    setSelected(userId);
    // Defaulting mirror of the server's U4 rule: picking a driver presets
    // their default vehicle; the select below is the per-job override.
    setVehicleId(userId ? (entryFor(userId)?.defaultVehicle?.id ?? '') : '');
  }

  // Pool vehicles + the selected driver's own — never another driver's.
  const eligibleVehicles = vehicles.filter(
    (v) => v.isActive && (!v.ownerDriverUserId || v.ownerDriverUserId === selected),
  );
  const selectedEntry = selected ? entryFor(selected) : undefined;

  return (
    <Dialog open={open} onClose={onClose} title="Assign driver">
      <div className="space-y-4">
        {drivers.length === 0 ? (
          <p className="text-sm text-breaker-mute">
            No drivers in this fleet. Invite one from the Team page.
          </p>
        ) : (
          <>
            <Field label="Driver" htmlFor="driver">
              <select
                id="driver"
                value={selected}
                onChange={(e) => onDriverChange(e.target.value)}
                className={cn(
                  'block w-full rounded-md bg-white px-3 h-10 text-sm text-breaker-ink',
                  'border border-breaker-line-2',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breaker-identity',
                )}
              >
                <option value="">— Unassigned —</option>
                {drivers.map((d) => (
                  <option key={d.userId} value={d.userId}>
                    {d.user.displayName} (@{d.user.handle})
                  </option>
                ))}
              </select>
            </Field>
            {selected ? (
              <Field label="Vehicle for this job" htmlFor="assignVehicle">
                <select
                  id="assignVehicle"
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  className={cn(
                    'block w-full rounded-md bg-white px-3 h-10 text-sm text-breaker-ink',
                    'border border-breaker-line-2',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breaker-identity',
                  )}
                >
                  <option value="">— No vehicle —</option>
                  {eligibleVehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.registration} · {vehicleTypeLabel(v.type)}
                      {v.ownerDriverUserId === selected
                        ? ' (own)'
                        : ' (pool)'}
                      {selectedEntry?.defaultVehicle?.id === v.id ? ' — default' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {selectedEntry?.compliance === 'blocked' ? (
              <p className="text-xs text-breaker-red">
                {selectedEntry.displayName} is a subcontractor with missing or expired
                cover — the server will refuse this assignment until it&rsquo;s recorded
                on their driver page.
              </p>
            ) : null}
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={busy}
            action={() =>
              onSelect({
                driverUserId: selected || null,
                vehicleId: selected ? vehicleId || null : null,
              })
            }
          >
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ============================================================
// Details card (customer name resolved)
// ============================================================

function DetailsCard({ fleetId, load }: { fleetId: string; load: Load }) {
  const customerQuery = useCustomer(fleetId, load.customerId);
  return (
    <Card title="Details">
      <DetailRow label="Customer">
        {load.customerId ? (
          customerQuery.data ? (
            <span className="text-breaker-ink">{customerQuery.data.name}</span>
          ) : (
            <span className="ref">{shortId(load.customerId)}</span>
          )
        ) : (
          <span className="text-breaker-mute">—</span>
        )}
      </DetailRow>
      <DetailRow label="Scheduled">
        <span className="text-breaker-ink-2">
          {formatDateTime(load.scheduledStartAt)}
        </span>
      </DetailRow>
      <DetailRow label="Total" last>
        <span className="tabular-nums font-medium">{formatMoney(load.totalPence)}</span>
      </DetailRow>
    </Card>
  );
}

// ============================================================
// Tracking link card
// ============================================================

function TrackingLinkCard({ fleetId, loadId }: { fleetId: string; loadId: string }) {
  const toast = useToast();
  const linksQuery = useTrackingLinks(fleetId, loadId);
  const create = useCreateTrackingLink({ fleetId });
  const revoke = useRevokeTrackingLink({ fleetId });

  // The plaintext URL is only available immediately after create.
  const [justCreated, setJustCreated] = useState<LoadTrackingLinkWithToken | null>(null);
  const [copied, setCopied] = useState(false);

  function handleCreate() {
    return create
      .mutateAsync({ loadId })
      .then(({ link }) => {
        setJustCreated(link);
        toast.success('Tracking link created');
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiError ? err.message : 'Couldn\u2019t create link');
      });
  }

  function handleRevoke(linkId: string) {
    return revoke
      .mutateAsync({ loadId, linkId })
      .then(() => {
        toast.success('Tracking link revoked');
        if (justCreated?.id === linkId) setJustCreated(null);
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiError ? err.message : 'Revoke failed');
      });
  }

  async function copyToClipboard(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Couldn\u2019t copy. Select the link and copy manually.');
    }
  }

  const activeLinks = (linksQuery.data ?? []).filter((l) => !l.revokedAt);

  return (
    <Card title="Tracking link">
      {justCreated ? (
        <div className="space-y-2">
          <p className="text-2xs uppercase tracking-wider text-breaker-mute">
            New link · share this URL with the customer
          </p>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 ref bg-breaker-ground-2 border border-breaker-line rounded px-2 py-1.5 break-all text-[11px] leading-snug">
              {justCreated.url}
            </code>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => copyToClipboard(justCreated.url)}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="text-2xs text-breaker-mute">
            The URL is shown once — it isn&rsquo;t re-displayable. Expires{' '}
            {formatDateTime(justCreated.expiresAt)}.
          </p>
          <div className="pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setJustCreated(null)}
              className="w-full"
            >
              Done
            </Button>
          </div>
        </div>
      ) : linksQuery.isLoading ? (
        <div className="py-2 flex justify-center">
          <Spinner size="sm" />
        </div>
      ) : activeLinks.length > 0 ? (
        <div className="space-y-3">
          {activeLinks.map((link) => (
            <TrackingLinkRow
              key={link.id}
              link={link}
              busy={revoke.isPending && revoke.variables?.linkId === link.id}
              onRevoke={() => handleRevoke(link.id)}
            />
          ))}
          <Button
            variant="secondary"
            size="sm"
            action={handleCreate}
            className="w-full"
          >
            Create another
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-breaker-mute">
            No active tracking link. Create one to share the live status with a customer.
          </p>
          <Button
            variant="primary"
            size="sm"
            action={handleCreate}
            className="w-full"
          >
            Create tracking link
          </Button>
        </div>
      )}
      <p className="text-2xs text-breaker-mute mt-3 pt-3 border-t border-breaker-line">
        Public page lives at{' '}
        <span className="ref text-[10px]">{config.trackUrl.replace(/^https?:\/\//, '')}/track/…</span>
      </p>
    </Card>
  );
}

function TrackingLinkRow({
  link,
  busy,
  onRevoke,
}: {
  link: LoadTrackingLink;
  busy: boolean;
  onRevoke(): Promise<unknown>;
}) {
  return (
    <div className="border border-breaker-line rounded-md p-3 bg-breaker-ground-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-breaker-ink">
            {link.label ?? 'Tracking link'}
          </p>
          <p className="text-2xs text-breaker-mute mt-0.5">
            Expires {formatDateTime(link.expiresAt)}
            {link.accessCount > 0 ? ` · viewed ${link.accessCount}×` : ' · not viewed yet'}
          </p>
        </div>
        <Button variant="ghost" size="sm" action={onRevoke} loading={busy}>
          Revoke
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Layout primitives
// ============================================================


function DetailRow({
  label,
  children,
  last,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between py-2.5',
        !last && 'border-b border-breaker-line',
      )}
    >
      <span className="text-xs text-breaker-mute">{label}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

// ============================================================
// Driver location card (consumes Phase 4.75d load-locations read)
// ============================================================

function DriverLocationCard({
  fleetId,
  load,
  stops,
}: {
  fleetId: string;
  load: Load;
  stops: LoadStop[];
}) {
  const assignedId = load.assignedDriverUserId ?? null;
  const locationsQuery = useLoadLocations(
    fleetId,
    assignedId ? load.id : null,
  );
  const membersQuery = useFleetMembers(fleetId);
  const driverName = useMemo(() => {
    if (!assignedId) return null;
    const m = indexMembers(membersQuery.data).get(assignedId);
    return m?.user.displayName ?? null;
  }, [assignedId, membersQuery.data]);

  // C4a: the map never hides — any stop with coordinates gives it something
  // to show (collection solid, delivery hollow), driver fixes or not.
  const stopPins = useMemo<MapStopPin[]>(
    () =>
      stops
        .filter((s) => s.lat !== null && s.lng !== null)
        .map((s) => ({
          lat: Number(s.lat),
          lng: Number(s.lng),
          type: s.type,
          label: [s.line1, s.city ?? s.postcode].filter(Boolean).join(', '),
        })),
    [stops],
  );

  if (!assignedId) {
    return (
      <Card title="Driver location">
        {stopPins.length > 0 ? (
          <div className="space-y-3">
            <LocationMap
              points={[]}
              stops={stopPins}
              className="rounded-md overflow-hidden border border-breaker-line"
            />
            <p className="text-sm text-breaker-mute italic">
              Assign a driver to see their shared location.
            </p>
          </div>
        ) : (
          <p className="text-sm text-breaker-mute italic py-1">
            Assign a driver to see their shared location.
          </p>
        )}
      </Card>
    );
  }

  const points = locationsQuery.data ?? [];
  const latest = points.length > 0 ? points[points.length - 1] : null;

  return (
    <Card title="Driver location">
      {locationsQuery.isLoading ? (
        <div className="py-8 flex justify-center">
          <Spinner size="sm" />
        </div>
      ) : locationsQuery.isError ? (
        <EmptyState
          title="Couldn't load location"
          description={(locationsQuery.error as Error)?.message ?? 'The server returned an error.'}
          action={
            <Button onClick={() => locationsQuery.refetch()} variant="secondary" size="sm">
              Retry
            </Button>
          }
        />
      ) : latest ? (
        <div className="space-y-3">
          <LocationMap
            points={points}
            stops={stopPins}
            className="rounded-md overflow-hidden border border-breaker-line"
          />
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <p className="text-xs text-breaker-ink-2">
              {driverName ? <span className="font-medium text-breaker-ink">{driverName}</span> : 'Driver'}
              {' · '}
              {points.length === 1 ? '1 point' : `${points.length} points`}
            </p>
            <p className="text-2xs text-breaker-mute">
              Last seen {formatRelative(latest.recordedAt)} · {formatDateTime(latest.recordedAt)}
            </p>
          </div>
        </div>
      ) : stopPins.length > 0 ? (
        // C4a: no fixes yet, but the stops give the map something to show.
        <div className="relative">
          <LocationMap
            points={[]}
            stops={stopPins}
            className="rounded-md overflow-hidden border border-breaker-line"
          />
          {/* z-[1000] clears Leaflet's internal panes so the pill floats above the tiles. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-3 z-[1000] flex justify-center px-3">
            <span className="rounded-full bg-white/95 border border-breaker-line shadow-sm px-3 py-1.5 text-xs text-breaker-ink-2 text-center">
              Waiting for driver location &mdash; it appears when they set off.
            </span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-breaker-mute py-1">
          {driverName ? `${driverName} hasn\u2019t` : 'This driver hasn\u2019t'} shared a location yet.
        </p>
      )}
    </Card>
  );
}

// ============================================================
// S50 - Stop add/edit dialog
// ============================================================

/** datetime-local <input> uses "YYYY-MM-DDTHH:mm" in local time; the API wants ISO-8601 UTC. */
function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
/** Inverse: ISO-8601 -> "YYYY-MM-DDTHH:mm" in local time for prefilling the input. */
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface StopFormDialogProps {
  fleetId: string;
  loadId: string;
  open: boolean;
  stop: LoadStop | null;
  onClose(): void;
}

function StopFormDialog({ fleetId, loadId, open, stop, onClose }: StopFormDialogProps) {
  const toast = useToast();
  const addStop = useAddStop({ fleetId });
  const updateStop = useUpdateStop({ fleetId });
  const editing = Boolean(stop);
  // Unit D: Enter-key re-submits are dropped while one is in flight.
  const submitLock = useActionLock();
  const busy = addStop.isPending || updateStop.isPending || submitLock.locked;

  const [type, setType] = useState<'pickup' | 'dropoff'>('dropoff');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [instructions, setInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Re-seed the form whenever the dialog opens or the target stop changes.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setType((stop?.type as 'pickup' | 'dropoff') ?? 'dropoff');
    setLine1(stop?.line1 ?? '');
    setLine2(stop?.line2 ?? '');
    setCity(stop?.city ?? '');
    setPostcode(stop?.postcode ?? '');
    setContactName(stop?.contactName ?? '');
    setContactPhone(stop?.contactPhoneE164 ?? '');
    setWindowStart(isoToLocalInput(stop?.windowStartAt));
    setWindowEnd(isoToLocalInput(stop?.windowEndAt));
    setInstructions(stop?.instructions ?? '');
  }, [open, stop]);

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!line1.trim()) {
      setError('Address line 1 is required.');
      return;
    }

    const body = {
      type,
      line1: line1.trim(),
      line2: line2.trim() || null,
      city: city.trim() || null,
      postcode: postcode.trim() || null,
      contactName: contactName.trim() || null,
      contactPhoneE164: contactPhone.trim() || null,
      windowStartAt: localInputToIso(windowStart),
      windowEndAt: localInputToIso(windowEnd),
      instructions: instructions.trim() || null,
    };

    const onErr = (err: unknown) =>
      toast.error(err instanceof ApiError ? err.message : 'Couldn\u2019t save stop');

    if (editing && stop) {
      submitLock.run(() =>
        updateStop
          .mutateAsync({ loadId, stopId: stop.id, body })
          .then(() => {
            toast.success('Stop updated');
            onClose();
          })
          .catch(onErr),
      );
    } else {
      submitLock.run(() =>
        addStop
          .mutateAsync({ loadId, body })
          .then(() => {
            toast.success('Stop added');
            onClose();
          })
          .catch(onErr),
      );
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={editing ? 'Edit stop' : 'Add stop'}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Type" htmlFor="stopType">
          <select
            id="stopType"
            value={type}
            onChange={(e) => setType(e.target.value as 'pickup' | 'dropoff')}
            className="block w-full rounded-md bg-white px-3 h-10 text-sm text-breaker-ink border border-breaker-line-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breaker-identity focus-visible:ring-offset-2 focus-visible:ring-offset-breaker-ground"
          >
            <option value="pickup">Pickup</option>
            <option value="dropoff">Dropoff</option>
          </select>
        </Field>

        <Field label="Address line 1" htmlFor="stopLine1" error={error ?? undefined}>
          <Input
            id="stopLine1"
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
            invalid={Boolean(error)}
            placeholder="e.g. Unit 4, Riverside Industrial Estate"
          />
        </Field>

        <Field label="Address line 2" htmlFor="stopLine2" hint="Optional">
          <Input id="stopLine2" value={line2} onChange={(e) => setLine2(e.target.value)} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="City / town" htmlFor="stopCity">
            <Input id="stopCity" value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <Field label="Postcode" htmlFor="stopPostcode">
            <Input
              id="stopPostcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Contact name" htmlFor="stopContactName" hint="Optional">
            <Input
              id="stopContactName"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
          </Field>
          <Field label="Contact phone" htmlFor="stopContactPhone" hint="E.164, e.g. +447700900123">
            <Input
              id="stopContactPhone"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+44..."
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Window from" htmlFor="stopWindowStart" hint="Required to assign">
            <Input
              id="stopWindowStart"
              type="datetime-local"
              value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)}
            />
          </Field>
          <Field label="Window to" htmlFor="stopWindowEnd" hint="Optional">
            <Input
              id="stopWindowEnd"
              type="datetime-local"
              value={windowEnd}
              onChange={(e) => setWindowEnd(e.target.value)}
            />
          </Field>
        </div>

        <Field label="Instructions" htmlFor="stopInstructions" hint="Access notes, parking, who to ask for...">
          <Textarea
            id="stopInstructions"
            rows={3}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </Field>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            {editing ? 'Save stop' : 'Add stop'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ============================================================
// S50 - Editable charges card
// ============================================================

/** pence (integer) -> pounds string for an <input>, e.g. 1500 -> "15.00" */
function penceToInput(pence: number | null | undefined): string {
  if (pence == null) return '';
  return (pence / 100).toFixed(2);
}
/** pounds string -> integer pence, e.g. "15.00" -> 1500. Returns 0 for blank/invalid. */
function inputToPence(value: string): number {
  const n = Number.parseFloat(value);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function ChargesCard({ fleetId, load }: { fleetId: string; load: Load }) {
  const toast = useToast();
  const update = useUpdateLoad({ fleetId });
  const [editing, setEditing] = useState(false);

  const [service, setService] = useState('');
  const [extras, setExtras] = useState('');
  const [vat, setVat] = useState('');

  useEffect(() => {
    if (!editing) return;
    setService(penceToInput(load.serviceChargePence));
    setExtras(penceToInput(load.extrasPence));
    setVat(penceToInput(load.vatPence));
  }, [editing, load]);

  const previewTotal =
    inputToPence(service) + inputToPence(extras) + inputToPence(vat);

  function save() {
    const serviceChargePence = inputToPence(service);
    const extrasPence = inputToPence(extras);
    const vatPence = inputToPence(vat);
    const totalPence = serviceChargePence + extrasPence + vatPence;
    return update
      .mutateAsync({
        loadId: load.id,
        body: { serviceChargePence, extrasPence, vatPence, totalPence },
      })
      .then(() => {
        toast.success('Charges updated');
        setEditing(false);
      })
      .catch((err: unknown) =>
        toast.error(err instanceof ApiError ? err.message : 'Couldn\u2019t save charges'),
      );
  }

  if (!editing) {
    return (
      <Card title="Charges">
        <div className="flex justify-end -mt-1 mb-3">
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            Edit charges
          </Button>
        </div>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-breaker-mute">Service charge</dt>
            <dd className="text-breaker-ink">{formatMoney(load.serviceChargePence)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-breaker-mute">Extras</dt>
            <dd className="text-breaker-ink">{formatMoney(load.extrasPence)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-breaker-mute">VAT</dt>
            <dd className="text-breaker-ink">{formatMoney(load.vatPence)}</dd>
          </div>
          <div className="flex justify-between border-t border-breaker-line pt-2 font-medium">
            <dt className="text-breaker-ink">Total</dt>
            <dd className="text-breaker-ink">{formatMoney(load.totalPence)}</dd>
          </div>
        </dl>
      </Card>
    );
  }

  return (
    <Card title="Charges">
      <div className="space-y-3">
        <Field label="Service charge (GBP)" htmlFor="chargeService">
          <Input
            id="chargeService"
            type="number"
            min="0"
            step="0.01"
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder="0.00"
          />
        </Field>
        <Field label="Extras (GBP)" htmlFor="chargeExtras">
          <Input
            id="chargeExtras"
            type="number"
            min="0"
            step="0.01"
            value={extras}
            onChange={(e) => setExtras(e.target.value)}
            placeholder="0.00"
          />
        </Field>
        <Field label="VAT (GBP)" htmlFor="chargeVat">
          <Input
            id="chargeVat"
            type="number"
            min="0"
            step="0.01"
            value={vat}
            onChange={(e) => setVat(e.target.value)}
            placeholder="0.00"
          />
        </Field>
        <div className="flex justify-between border-t border-breaker-line pt-2 text-sm font-medium">
          <span className="text-breaker-ink">Total</span>
          <span className="text-breaker-ink">{formatMoney(previewTotal)}</span>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={() => setEditing(false)} disabled={update.isPending}>
            Cancel
          </Button>
          <Button type="button" action={save}>
            Save charges
          </Button>
        </div>
      </div>
    </Card>
  );
}
